'use client';

import { RefObject } from 'react';

import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';

import { loadScrollTrigger } from '@/utils/gsap/lazyPlugins';

interface RevealAnimationProps {
  triggerRef: RefObject<HTMLDivElement | null>;
  triggerOnce?: boolean;
}

const useRevealAnimation = ({ triggerRef, triggerOnce = true }: RevealAnimationProps) => {
  useGSAP(
    () => {
      const container = triggerRef.current;

      if (!container) return undefined;

      let isCancelled = false;

      const initScrollTrigger = async () => {
        await loadScrollTrigger();

        if (isCancelled || !container) return;

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: container,
            toggleActions: triggerOnce ? 'play none none none' : 'play none none reset',
          },
        });

        tl.set(container, { autoAlpha: 0, y: 100 }).to(container, {
          duration: 1.5,
          autoAlpha: 1,
          y: 0,
          ease: 'power2.out',
          delay: 0.3,
        });
      };

      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            initScrollTrigger();
            observer.disconnect();
          }
        },
        { rootMargin: '200px' }
      );

      observer.observe(container);

      return () => {
        isCancelled = true;
        observer.disconnect();
      };
    },
    { scope: triggerRef }
  );
};

export default useRevealAnimation;
